import { Box, Card, Grid, useMediaQuery } from '@material-ui/core'
import MuiLink from '@material-ui/core/Link'
import { makeStyles } from '@material-ui/core/styles'
import {
  AccessTime as AccessTimeIcon,
  Label as LabelIcon,
  Public as PublicIcon,
  SvgIconComponent,
  Today as TodayIcon,
} from '@material-ui/icons'
import { isEqual } from 'lodash'
import { DateTime } from 'luxon'
import Link from 'next/link'
import React from 'react'

import { useAnalytics } from '../../lib/analytics'
import { Actions, Categories } from '../../lib/analytics/events'
import { Theme } from '../../lib/theming/Theme'
import { Button } from '../button'
import IconText from '../utils/IconText'
import { H4 } from '../utils/typography'
import ParticipantsList from './ParticipantsList'

export type ParticipantProps = {
  avatarUrl?: string
  companyName?: string
  id: string
  jobTitle?: string
  name: string
}

export type TopicProps = {
  id: string
  name: string
}

export type TimeslotProps = {
  endsAt: DateTime
  format?: string
  live?: boolean
  liveURL?: string
  location: {
    icon?: SvgIconComponent
    id: string
    name: string
  }
  participants: Array<ParticipantProps>
  slug: string
  startsAt: DateTime
  tags: Array<string>
  title: string
  topics: Array<TopicProps>
  videoURL?: string
}

const useStyles = makeStyles<Theme>(({ palette, spacing }) => ({
  actionButton: {
    flexGrow: 0,
    maxWidth: '150px',
    padding: spacing(1.5, 2),
    textAlign: 'center',
    width: '100%',
  },
  buttonItem: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: spacing(0, 1),
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
  },
  container: {
    '&:hover': {
      '& $timeslot': {
        border: `1px solid ${palette.grey[100]}`,
        boxShadow: `0 2px 16px 0 ${palette.grey.A100}`,
      },
    },
    position: 'relative',
    zIndex: 1,
  },
  liveButton: {
    '&:hover': {
      backgroundColor: palette.live.light,
    },
    backgroundColor: palette.live.main,
    color: palette.live.contrastText,
  },
  timeslot: {
    border: `1px solid ${palette.grey.A100}`,
    boxShadow: 'none',
    color: 'inherit',
    display: 'block',
    position: 'relative',
    textDecoration: 'inherit',
    width: '100%',
  },
}))

const durationFor = ({ endsAt, startsAt }: { endsAt: DateTime; startsAt: DateTime }) => {
  const diff = endsAt.diff(startsAt, ['hours', 'minutes']).toObject()
  const durationHours = diff.hours && diff.hours > 0 ? `${diff.hours}hr` : ''
  const durationMins = diff.minutes && diff.minutes > 0 ? `${diff.minutes}mins` : ''

  let duration = (durationHours && durationHours) || (durationMins && durationMins)
  if (durationHours && durationMins) duration += ` ${durationMins}`

  return duration
}

const Timeslot = ({
  participants,
  title,
  topics,
  slug: timeslotSlug,
  location: { name: locationName },
  liveURL,
  videoURL,
  endsAt,
  startsAt,
  tags,
  live = false,
}: TimeslotProps) => {
  const hasCFHLinks = process.env.ENABLE_CFH_LINKS
  const classes = useStyles()
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const duration = durationFor({ endsAt, startsAt })
  const { day, monthLong } = startsAt

  const { trackEvent } = useAnalytics()

  const cardTitleId = `Timeslot__title--${timeslotSlug}`

  const conferenceSlug = tags[0] || process.env.DEFAULT_CONFERENCE_SLUG || ''

  const ActionButton = () => {
    if (liveURL && live) {
      return (
        <Grid item classes={{ root: classes.buttonItem }} xs={2}>
          <MuiLink
            href={hasCFHLinks ? liveURL : '/'}
            rel="noreferrer"
            target="_blank"
            underline="none"
          >
            <Button className={`${classes.actionButton} ${classes.liveButton}`} variant="contained">
              Live now
            </Button>
          </MuiLink>
        </Grid>
      )
    }
    if (videoURL) {
      return (
        <></>
        // <Grid item classes={{ root: classes.buttonItem }} xs={2}>
        //   <MuiLink href={videoURL} rel="noreferrer" target="_blank" underline="none">
        //     <Button className={classes.actionButton} variant="contained">
        //       Watch now
        //     </Button>
        //   </MuiLink>
        // </Grid>
      )
    }
    return null
  }

  return (
    <Grid container classes={{ root: classes.container }}>
      <Link
        passHref
        as={`/schedule/${conferenceSlug}/timeslot/${timeslotSlug}`}
        href="/schedule/[conferenceSlug]/timeslot/[timeslotSlug]"
      >
        <Card
          aria-labelledby={cardTitleId}
          classes={{ root: classes.timeslot }}
          component="a"
          onClick={() => {
            trackEvent({
              action: Actions.TimeslotOpened,
              category: Categories.Schedule,
              label: timeslotSlug,
              location: Categories.Schedule,
            })
          }}
        >
          <Grid item xs={10}>
            <Box p={mdUp ? 3 : 2}>
              <Box display="flex" flexWrap="wrap">
                <IconText icon={PublicIcon} text={locationName} />
                <Box display="flex" flexWrap="nowrap">
                  <IconText color="textSecondary" icon={TodayIcon} text={`${day} ${monthLong}`} />
                  <IconText color="textSecondary" icon={AccessTimeIcon} text={duration} />
                </Box>
              </Box>
              <H4 component="h2" id={cardTitleId} mb={2} mr={2} mt={1}>
                {title}
              </H4>
              {participants.length > 0 && (
                <ParticipantsList limitVisible={3} participants={participants} />
              )}
              {mdUp && topics.length > 0 && (
                <Box display="flex" flexDirection={['column', 'row']} flexWrap="wrap">
                  {topics.map(({ id: topicId, name }) => (
                    <IconText key={topicId} color="textSecondary" icon={LabelIcon} text={name} />
                  ))}
                </Box>
              )}
            </Box>
          </Grid>
        </Card>
      </Link>
      {mdUp && <ActionButton />}
    </Grid>
  )
}

export default React.memo(Timeslot, isEqual)
